import React, { useEffect, useState } from 'react';
import sampleData from './sampleData';

// ─── tiny design tokens (no external dep) ────────────────────────────────────
const tokens = {
  blue:    '#0073bb',
  blueLight: '#e8f4fd',
  gray50:  '#f9f9f8',
  gray100: '#f1efe8',
  gray200: '#d3d1c7',
  gray600: '#5f5e5a',
  gray800: '#2c2c2a',
  teal:    '#0f6e56',
  tealLight: '#e1f5ee',
  radius:  '10px',
  radiusSm: '6px',
};

// ─── shared micro-components ─────────────────────────────────────────────────
const SectionHeading = ({ children }) => (
  <h2 style={{
    fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: tokens.gray600,
    borderBottom: `1px solid ${tokens.gray200}`,
    paddingBottom: 6, marginBottom: 16, marginTop: 0,
  }}>{children}</h2>
);

const Badge = ({ children, color = tokens.blueLight, textColor = tokens.blue }) => (
  <span style={{
    display: 'inline-block', fontSize: 11, padding: '2px 8px',
    borderRadius: 20, background: color, color: textColor,
    marginRight: 4, marginBottom: 4, fontWeight: 500,
  }}>{children}</span>
);

const Card = ({ children, style = {}, print = false }) => (
  <div style={{
    background: '#fff',
    border: print ? 'none' : `0.5px solid ${tokens.gray200}`,
    borderRadius: print ? 0 : tokens.radius,
    padding: print ? '0 0 12px' : '1.1rem 1.25rem',
    marginBottom: 14, ...style,
  }}>{children}</div>
);

// ─── print stylesheet injected once ──────────────────────────────────────────
const PRINT_CSS = `
@media print {
  @page { margin: 14mm 14mm 14mm 14mm; size: A4; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  .page-break { page-break-before: always; }
  a { color: inherit !important; text-decoration: none !important; }
}
.print-only { display: none; }
`;

// ─── main component ───────────────────────────────────────────────────────────
export default function ResumeTemplate({ data = sampleData }) {
  const [printMode, setPrintMode]                 = useState(false);
  const [showNotableProjects, setShowNotableProjects] = useState(true);
  const [showMicrosoftFull, setShowMicrosoftFull] = useState(false);
  const [showOthersFull, setShowOthersFull]       = useState(false);

  // inject print CSS once
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = PRINT_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // trigger print
  useEffect(() => {
    if (!printMode) return;
    const id = requestAnimationFrame(() => window.print());
    return () => cancelAnimationFrame(id);
  }, [printMode]);

  useEffect(() => {
    const after = () => setPrintMode(false);
    window.addEventListener('afterprint', after);
    return () => window.removeEventListener('afterprint', after);
  }, []);

  // ── cert buckets ────────────────────────────────────────────────────────────
  const sorted = [...data.certifications].sort((a, b) => Number(b.date) - Number(a.date));
  const awsCerts     = sorted.filter(c => c.provider === 'Amazon Web Services' && c.certification.includes('Certified'));
  const awsKnowledge = sorted.filter(c => c.provider === 'Amazon Web Services' && c.certification.includes('Knowledge'));
  const msCertsRecent  = sorted.filter(c => c.provider === 'Microsoft' && Number(c.date) >= 2017);
  const msCertsAll     = sorted.filter(c => c.provider === 'Microsoft');
  const msLogosRecent  = msCertsRecent.filter(c => c.logo);
  const msLogosAll     = msCertsAll.filter(c => c.logo);
  const otherRecent    = sorted.filter(c => c.provider !== 'Microsoft' && c.provider !== 'Amazon Web Services' && Number(c.date) >= 2017);
  const otherAll       = sorted.filter(c => c.provider !== 'Microsoft' && c.provider !== 'Amazon Web Services');
  const otherLogosRecent = otherRecent.filter(c => c.logo);
  const otherLogosAll    = otherAll.filter(c => c.logo);

  const msCerts   = showMicrosoftFull ? msCertsAll   : msCertsRecent;
  const msLogos   = showMicrosoftFull ? msLogosAll   : msLogosRecent;
  const otherCerts  = showOthersFull  ? otherAll     : otherRecent;
  const otherLogos  = showOthersFull  ? otherLogosAll : otherLogosRecent;

  // ── helpers ─────────────────────────────────────────────────────────────────
  const ExperienceArticle = ({ item, isPrint }) => {
    const max = isPrint
      ? Math.min(item.maxBullets ?? item.responsibilities.length, item.responsibilities.length)
      : item.responsibilities.length;
    return (
      <article style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 10 }}>
          {item.logo && (
            <img src={item.logo} alt={item.company}
              style={{ width: 44, height: 44, objectFit: 'contain', flexShrink: 0 }} />
          )}
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: tokens.gray800 }}>{item.role}</div>
            <div style={{ fontSize: 13, color: tokens.gray600 }}>
              <strong style={{ color: tokens.gray800 }}>{item.company}</strong> · {item.period} · {item.location}
            </div>
          </div>
        </div>
        <p style={{ fontSize: 13, color: tokens.gray800, margin: '0 0 8px', lineHeight: 1.6 }}>
          <strong>{item.mainStrong}</strong> {item.main}
        </p>
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {item.responsibilities.slice(0, max).map((r, i) => (
            <li key={i} style={{ fontSize: 13, color: tokens.gray800, marginBottom: 4, lineHeight: 1.55 }}>
              <strong>{r.lead}</strong> {r.detail}
            </li>
          ))}
        </ul>
      </article>
    );
  };

  // ── layout vars ─────────────────────────────────────────────────────────────
  const isPrint = printMode;

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', color: tokens.gray800, maxWidth: 960, margin: '0 auto', padding: isPrint ? 0 : '24px 16px' }}>

      {/* ── HERO ── */}
      <div style={{
        background: isPrint ? '#fff' : `linear-gradient(135deg, #0a2540 0%, #0f4c81 100%)`,
        color: isPrint ? tokens.gray800 : '#fff',
        borderRadius: isPrint ? 0 : tokens.radius,
        padding: isPrint ? '0 0 16px' : '2rem 2rem 1.5rem',
        marginBottom: isPrint ? 16 : 24,
        border: isPrint ? `none` : 'none',
      }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ fontSize: isPrint ? 22 : 28, fontWeight: 700, margin: '0 0 4px', color: isPrint ? tokens.gray800 : '#fff' }}>
              {data.name}
            </h1>
            <div style={{ fontSize: isPrint ? 13 : 16, opacity: isPrint ? 1 : 0.9, color: isPrint ? tokens.blue : '#a8d4f5', fontWeight: 500, marginBottom: 12 }}>
              {data.role} — {data.location}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, opacity: isPrint ? 1 : 0.85 }}>
              <span>{data.email}</span>
              <span>{data.phone}</span>
              <a href={data.website} style={{ color: isPrint ? tokens.blue : '#7ec8e3', textDecoration: 'none' }}>{data.website}</a>
            </div>
          </div>
          {!isPrint && (
            <div className="no-print" style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginRight: 4 }}>Built in React · Generates a formatted PDF</span>
              <button
                onClick={() => setPrintMode(true)}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                  color: '#fff', borderRadius: tokens.radiusSm, padding: '7px 16px',
                  fontSize: 13, cursor: 'pointer', fontWeight: 500,
                }}>
                Print / Save as PDF
              </button>
            </div>
          )}
        </div>

        {/* summary in hero for screen, below for print */}
        {!isPrint && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
            {data.summary.map((s, i) => (
              <p key={i} style={{ margin: '0 0 6px', fontSize: 14, opacity: 0.88, lineHeight: 1.65, color: '#e8f1fb' }}>{s}</p>
            ))}
          </div>
        )}
      </div>

      {/* ── PRINT SUMMARY ── */}
      {isPrint && (
        <>
          <div className="print-only" style={{ marginBottom: 14 }}>
            <SectionHeading>Summary</SectionHeading>
            {data.summary.map((s, i) => (
              <p key={i} style={{ fontSize: 12, margin: '0 0 4px', lineHeight: 1.55 }}>{s}</p>
            ))}
          </div>
          <div className="print-only" style={{
            marginBottom: 20,
            background: '#0a2540',
            borderRadius: tokens.radiusSm,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 3 }}>
                See this CV in full — with live demos, videos, and notable projects
              </div>
              <div style={{ fontSize: 11, color: '#a8d4f5', lineHeight: 1.5 }}>
                Interactive bot demos · Video walkthroughs · 10+ notable client projects · Full certification history
              </div>
            </div>
            <div style={{
              flexShrink: 0,
              background: '#fff',
              borderRadius: tokens.radiusSm,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 700,
              color: '#0a2540',
              whiteSpace: 'nowrap',
            }}>
              {data.website}
            </div>
          </div>
        </>
      )}

      {/* ── SCREEN: STATS BAR ── */}
      {!isPrint && (
        <div className="no-print" style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 12, marginBottom: 24,
        }}>
          {[
            { label: 'Years experience', value: '25+' },
            { label: 'Countries deployed', value: '63' },
            { label: 'AWS certifications', value: '8' },
            { label: 'AWS blogs published', value: '3+' },
          ].map(stat => (
            <div key={stat.label} style={{
              background: tokens.gray50, border: `0.5px solid ${tokens.gray200}`,
              borderRadius: tokens.radius, padding: '14px 16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: tokens.blue }}>{stat.value}</div>
              <div style={{ fontSize: 11, color: tokens.gray600, marginTop: 2 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── SCREEN: DEMOS + VIDEOS ── */}
      {!isPrint && data.demos.length > 0 && (
        <div className="no-print" style={{ marginBottom: 24 }}>
          <Card>
            <SectionHeading>Interactive bot demos</SectionHeading>
            <p style={{ fontSize: 13, color: tokens.gray600, margin: '0 0 14px' }}>
              Live demos built on Cognigy and AWS — try them in your browser. Best experienced on desktop with a microphone and headphones.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, alignItems: 'stretch' }}>
              {data.demos.map(demo => (
                <a key={demo.link} href={demo.link} target="_blank" rel="noopener noreferrer"
                  style={{ textDecoration: 'none', display: 'flex' }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column', width: '100%',
                    background: tokens.tealLight, border: `1px solid #9fe1cb`,
                    borderRadius: tokens.radiusSm, padding: '10px 14px', cursor: 'pointer',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: tokens.teal, marginBottom: 4 }}>{demo.title}</div>
                    <div style={{ fontSize: 12, color: tokens.gray600, lineHeight: 1.5, flex: 1 }}>{demo.description}</div>
                  </div>
                </a>
              ))}
            </div>
          </Card>
        </div>
      )}

      {!isPrint && data.videos.length > 0 && (
        <div className="no-print" style={{ marginBottom: 24 }}>
          <Card>
            <SectionHeading>Video demos</SectionHeading>
            <div style={{
              display: 'grid', gap: 20,
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}>
              {data.videos.map(v => (
                <div key={v.link}>
                  <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: tokens.radiusSm, overflow: 'hidden' }}>
                    <iframe src={v.link} title={v.title} frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: tokens.gray800 }}>{v.title}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* ── TWO-COLUMN LAYOUT (screen) / single col (print) ── */}
      <div style={{
        display: isPrint ? 'block' : 'grid',
        gridTemplateColumns: isPrint ? undefined : '220px 1fr',
        gap: 24,
        alignItems: 'start',
      }}>

        {/* ── LEFT SIDEBAR ── */}
        <aside>

          {/* Skills */}
          {data.skills.length > 0 && (
            <Card style={{ marginBottom: 14 }} print={isPrint}>
              <SectionHeading>Skills</SectionHeading>
              {data.skills.map(s => (
                <div key={s} style={{ fontSize: 13, color: tokens.gray800, marginBottom: 5, lineHeight: 1.4 }}>• {s}</div>
              ))}
            </Card>
          )}

          {/* Tech */}
          {data.tech.length > 0 && (
            <Card style={{ marginBottom: 14 }} print={isPrint}>
              <SectionHeading>Tech</SectionHeading>
              {data.tech.map(t => (
                <div key={t} style={{ fontSize: 12, color: tokens.gray800, marginBottom: 5, lineHeight: 1.5 }}>• {t}</div>
              ))}
            </Card>
          )}

          {/* Blogs - sidebar on screen */}
          {!isPrint && data.blogs.length > 0 && (
            <Card className="no-print">
              <SectionHeading>Industry blogs</SectionHeading>
              {data.blogs.map(b => (
                <a key={b.link} href={b.link} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, textDecoration: 'none', color: tokens.blue, fontSize: 12, lineHeight: 1.4 }}>
                  <img src={b.icon} alt={b.vendor} style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }} />
                  {b.title}
                </a>
              ))}
            </Card>
          )}
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main>

          {/* Experience */}
          {data.experience.length > 0 && (
            <Card print={isPrint}>
              <SectionHeading>Experience</SectionHeading>
              {data.experience.map(exp => (
                <ExperienceArticle key={exp.role + exp.company} item={exp} isPrint={isPrint} />
              ))}
            </Card>
          )}

          {/* Notable Projects */}
          {data.projects.length > 0 && !isPrint && (
            <div className="no-print">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: tokens.gray800 }}>Notable projects</span>
                <button onClick={() => setShowNotableProjects(v => !v)}
                  style={{
                    fontSize: 12, background: 'none', border: `0.5px solid ${tokens.gray200}`,
                    borderRadius: tokens.radiusSm, padding: '4px 10px', cursor: 'pointer', color: tokens.blue,
                  }}>
                  {showNotableProjects ? 'Hide projects' : 'Show projects'}
                </button>
              </div>
              {showNotableProjects && (
                <Card>
                  {data.projects.map(p => (
                    <ExperienceArticle key={p.role + p.company} item={p} isPrint={false} />
                  ))}
                </Card>
              )}
            </div>
          )}

          {/* Education */}
          {data.education && data.education.length > 0 && (
            <Card print={isPrint}>
              <SectionHeading>Education</SectionHeading>
              {data.education.map(edu => (
                <div key={edu.degree} style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                  {edu.logo && <img src={edu.logo} alt={edu.institution} style={{ width: 44, height: 44, objectFit: 'contain', flexShrink: 0 }} />}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{edu.degree}</div>
                    <div style={{ fontSize: 13, color: tokens.gray600 }}>{edu.institution} · {edu.period}</div>
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Certifications */}
          {data.certifications.length > 0 && (
            <Card print={isPrint}>
              <SectionHeading>{isPrint ? 'Certifications (summary)' : 'Certifications'}</SectionHeading>

              {isPrint ? (
                <div>
                  {data.printCertifications.map((group, i) =>
                    Object.entries(group).map(([provider, certs]) => (
                      <div key={`${provider}-${i}`} style={{ fontSize: 12, marginBottom: 5 }}>
                        <strong>{provider}:</strong> {certs}
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <>
                  {/* AWS Certified */}
                  {awsCerts.length > 0 && (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                        {awsCerts.map(c => <img key={c.certification} src={c.logo} alt={c.certification} style={{ width: 70, height: 70, objectFit: 'contain' }} />)}
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        {awsCerts.map(c => (
                          <div key={c.certification} style={{ fontSize: 12, color: tokens.gray800, marginBottom: 2 }}>
                            {c.certification} <span style={{ color: tokens.gray600 }}>· {c.date}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {/* AWS Knowledge */}
                  {awsKnowledge.length > 0 && (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                        {awsKnowledge.map(c => <img key={c.certification} src={c.logo} alt={c.certification} style={{ width: 70, height: 70, objectFit: 'contain' }} />)}
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        {awsKnowledge.map(c => (
                          <div key={c.certification} style={{ fontSize: 12, color: tokens.gray800, marginBottom: 2 }}>
                            {c.certification} <span style={{ color: tokens.gray600 }}>· {c.date}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {/* Microsoft */}
                  {msLogos.length > 0 && (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                        {msLogos.map(c => <img key={c.certification} src={c.logo} alt={c.certification} style={{ width: 70, height: 70, objectFit: 'contain' }} />)}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        {msCerts.map(c => (
                          <div key={c.certification} style={{ fontSize: 12, marginBottom: 2 }}>
                            {c.certification} <span style={{ color: tokens.gray600 }}>· {c.date}</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => setShowMicrosoftFull(v => !v)}
                        style={{ fontSize: 12, background: 'none', border: `0.5px solid ${tokens.gray200}`, borderRadius: tokens.radiusSm, padding: '3px 10px', cursor: 'pointer', color: tokens.blue, marginBottom: 14 }}>
                        {showMicrosoftFull ? 'Show recent Microsoft only' : 'Show all Microsoft certifications'}
                      </button>
                    </>
                  )}
                  {/* Others */}
                  {otherLogos.length > 0 && (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                        {otherLogos.map(c => <img key={c.certification} src={c.logo} alt={c.certification} style={{ width: 70, height: 70, objectFit: 'contain' }} />)}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        {otherCerts.map(c => (
                          <div key={c.certification} style={{ fontSize: 12, marginBottom: 2 }}>
                            {c.certification} <span style={{ color: tokens.gray600 }}>· {c.date}</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => setShowOthersFull(v => !v)}
                        style={{ fontSize: 12, background: 'none', border: `0.5px solid ${tokens.gray200}`, borderRadius: tokens.radiusSm, padding: '3px 10px', cursor: 'pointer', color: tokens.blue }}>
                        {showOthersFull ? 'Show recent only' : 'Show all other certifications'}
                      </button>
                    </>
                  )}
                </>
              )}
            </Card>
          )}
        </main>
      </div>

      {/* ── FOOTER ── */}
      {!isPrint && (
        <div className="no-print" style={{ textAlign: 'center', padding: '24px 0 8px', borderTop: `1px solid ${tokens.gray200}`, marginTop: 8 }}>
          <button onClick={() => setPrintMode(true)}
            style={{
              background: tokens.blue, color: '#fff', border: 'none',
              borderRadius: tokens.radiusSm, padding: '9px 22px',
              fontSize: 14, cursor: 'pointer', fontWeight: 500,
            }}>
            Print / Save as PDF
          </button>
          <div style={{ fontSize: 11, color: tokens.gray600, marginTop: 8 }}>
            Built in React · Generates a clean multi-page PDF
          </div>
        </div>
      )}
    </div>
  );
}
